import { importOfacFiles } from "../utils/ofacImport";
import TreatyCountriesEditor from '../components/fatca/TreatyCountriesEditor';
import GiinDirectoryUpload from '../components/fatca/GiinDirectoryUpload';
import CorporateDocumentRequirementsEditor from '../components/admin/CorporateDocumentRequirementsEditor';
import { SanctionsActiveSource, SanctionsSourceStatusBadge, SanctionsSourceAgeBadge, SanctionsSourceReferences, SanctionsSourceUpdateButton, requestSanctionsSourceRefresh } from '../components/admin/SanctionsSourceRefreshInfo';
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase"; // ✅ match your other admin pages path
import { signOut } from "firebase/auth";
import { MYKYC_API_BASE } from "../config/api";

import { motion } from "framer-motion";
import { Toast } from "primereact/toast";

import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

import Papa from "papaparse";
import * as XLSX from "xlsx";

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { normalizeCountryLookupName } from "../utils/iso2to3";

import "./css/AdminUsersPage.css";

countries.registerLocale(enLocale);

// ******************* VARIABLES ********************

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const LISTS = [
  {
    key: "ofac",
    title: "OFAC",
    accept: ".xml",
    help: "Upload SDN.XML",
  },
  {
    key: "un",
    title: "UN Sanctions",
    accept: ".xml",
    help: "Upload UN consolidated list (XML).",
  },
  {
    key: "uae",
    title: "UAE Sanctions",
    accept: ".xls,.xlsx",
    help: "Upload UAE sanctions list (XLS).",
  },
];

// ******************* HELPERS ********************

const PROOF_OF_ADDRESS_ISSUER_COLLECTION = "proofOfAddressIssuers";
const PROOF_OF_ADDRESS_ISSUER_IMPORT_META_COLLECTION = "proofOfAddressIssuerImports";

function normalizeUtilityIssuerText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\b(LTD|LIMITED|LLC|INC|CORP|CORPORATION|COMPANY|CO|PLC|PJSC|SA|AG)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function utilityIssuerDocId(countryIso2, normalizedName) {
  const country = String(countryIso2 || "").trim().toUpperCase() || "XX";
  const slug =
    normalizeUtilityIssuerText(normalizedName)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 120) || "issuer";
  return `${country}_${slug}`;
}

function normalizeUtilityType(value) {
  const clean = String(value || "").trim().toLowerCase();
  if (!clean) return "";
  if (/electric/.test(clean)) return "electricity";
  if (/\bgas\b/.test(clean)) return "gas";
  if (/water/.test(clean)) return "water";
  if (/telecom|phone|mobile/.test(clean)) return "telecom";
  if (/internet|broadband/.test(clean)) return "internet";
  if (/municip/.test(clean)) return "municipal";
  if (/cooling|district/.test(clean)) return "district_cooling";
  return clean.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function utilityTypeLabel(value) {
  const key = normalizeUtilityType(value);
  const labels = {
    electricity: "Electricity",
    gas: "Gas",
    water: "Water",
    telecom: "Telecom",
    internet: "Internet",
    municipal: "Municipal",
    district_cooling: "District cooling",
  };
  return labels[key] || String(value || key || "Other").replace(/_/g, " ");
}

function incrementUtilityTypeCount(counts, type) {
  const key = normalizeUtilityType(type) || "other";
  counts[key] = (counts[key] || 0) + 1;
}

function sortedUtilityTypeCounts(typeCounts) {
  return Object.entries(typeCounts || {})
    .filter(([, count]) => Number(count) > 0)
    .sort(([a], [b]) => utilityTypeLabel(a).localeCompare(utilityTypeLabel(b)));
}

function utilityIssuerAliases(companyName) {
  const raw = String(companyName || "").trim();
  const aliases = new Set();
  const add = (value) => {
    const clean = String(value || "").trim();
    if (clean) aliases.add(clean);
  };

  add(raw);
  add(raw.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim());

  const parentheticalMatches = raw.matchAll(/\(([^)]+)\)/g);
  for (const match of parentheticalMatches) {
    const text = String(match[1] || "").trim();
    add(text);
    add(text.replace(/[^A-Za-z0-9]/g, ""));
  }

  add(raw.replace(/&/g, "and"));
  add(raw.replace(/\band\b/gi, "&"));
  add(raw.replace(/[^A-Za-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim());

  return Array.from(aliases).filter(Boolean);
}

function findHeaderIndex(headers, candidates) {
  const normalized = headers.map((header) =>
    String(header || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "")
  );
  return normalized.findIndex((header) =>
    candidates.some((candidate) => header === candidate)
  );
}

async function parseUtilityIssuerWorkbook(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  let headerRowIdx = -1;
  let headerIndexes = null;
  for (let i = 0; i < rows.length; i += 1) {
    const headers = rows[i] || [];
    const isoIdx = findHeaderIndex(headers, ["iso2"]);
    const countryIdx = findHeaderIndex(headers, ["country"]);
    const companyIdx = findHeaderIndex(headers, ["company", "issuer", "issuername"]);
    const typeIdx = findHeaderIndex(headers, ["utilitytype", "type", "issuertype"]);
    if (isoIdx >= 0 && countryIdx >= 0 && companyIdx >= 0 && typeIdx >= 0) {
      headerRowIdx = i;
      headerIndexes = { isoIdx, countryIdx, companyIdx, typeIdx };
      break;
    }
  }

  if (headerRowIdx < 0 || !headerIndexes) {
    throw new Error('No header row found. Expected columns: ISO2, Country, Company, Utility Type.');
  }

  const merged = new Map();
  const typeCounts = {};
  let sourceRowCount = 0;
  const skippedRows = [];
  const dataRows = rows.slice(headerRowIdx + 1);

  dataRows.forEach((row, idx) => {
    const countryIso2 = String(row[headerIndexes.isoIdx] || "").trim().toUpperCase();
    const countryName = String(row[headerIndexes.countryIdx] || "").trim();
    const issuerName = String(row[headerIndexes.companyIdx] || "").trim();
    const issuerType = normalizeUtilityType(row[headerIndexes.typeIdx]);
    if (!countryIso2 && !countryName && !issuerName && !issuerType) return;
    sourceRowCount += 1;
    if (!/^[A-Z]{2}$/.test(countryIso2) || !issuerName) {
      skippedRows.push(idx + headerRowIdx + 2);
      return;
    }

    incrementUtilityTypeCount(typeCounts, issuerType);
    const issuerNameNormalized = normalizeUtilityIssuerText(issuerName);
    const id = utilityIssuerDocId(countryIso2, issuerNameNormalized);
    if (!merged.has(id)) {
      const aliases = utilityIssuerAliases(issuerName);
      merged.set(id, {
        id,
        countryIso2,
        countryName,
        issuerName,
        issuerNameNormalized,
        issuerTypes: new Set(),
        aliases: new Set(aliases),
        aliasesNormalized: new Set(aliases.map(normalizeUtilityIssuerText).filter(Boolean)),
      });
    }

    const item = merged.get(id);
    if (countryName && !item.countryName) item.countryName = countryName;
    if (issuerType) item.issuerTypes.add(issuerType);
  });

  const issuers = Array.from(merged.values()).map((item) => ({
    id: item.id,
    countryIso2: item.countryIso2,
    countryName: item.countryName,
    issuerName: item.issuerName,
    issuerNameNormalized: item.issuerNameNormalized,
    issuerTypes: Array.from(item.issuerTypes).sort(),
    aliases: Array.from(item.aliases).sort((a, b) => a.localeCompare(b)),
    aliasesNormalized: Array.from(item.aliasesNormalized).sort((a, b) => a.localeCompare(b)),
    status: "verified",
    confidence: 1,
    source: "xlsx_import",
  }));

  return { issuers, sourceRowCount, skippedRows, typeCounts };
}


async function commitFirestoreOperations(ops, chunkSize = 450) {
  for (let i = 0; i < ops.length; i += chunkSize) {
    const batch = writeBatch(db);
    ops.slice(i, i + chunkSize).forEach((op) => {
      if (op.type === "delete") batch.delete(op.ref);
      if (op.type === "set") batch.set(op.ref, op.data, op.options || undefined);
    });
    await batch.commit();
  }
}

function formatAdminDateTime(value) {
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : value
      ? new Date(value)
      : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function prettyBytes(bytes) {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = Number(bytes);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function nowStamp() {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(
    d.getHours()
  )}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function parseSpreadsheetNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, "").replace(",", ".");
  const score = Number(normalized);
  return Number.isFinite(score) ? score : null;
}

// HELPER TO DEBUG AND LOG ERRORS DETAILS
function logFbErr(label, e) {
  console.group(`[${label}] Firebase error`);
  console.log("message:", e?.message);
  console.log("code:", e?.code);
  console.log("name:", e?.name);
  console.log("stack:", e?.stack);
  console.groupEnd();
}

// ******************* EXPORT DEFAULT FUNCTION ********************

export default function AdminSourcesUploadPage() {
  const nav = useNavigate();
  const toastRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [refreshRequestByKey, setRefreshRequestByKey] = useState({});
  const [sourceAgeNow, setSourceAgeNow] = useState(Date.now);

  useEffect(() => {
    const timer = setInterval(() => setSourceAgeNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const [meta, setMeta] = useState({
    ofac: null,
    un: null,
    uae: null,
  });

  const [fileByKey, setFileByKey] = useState({
    ofac: null,
    un: null,
    uae: null,
  });
  const [ofacFiles, setOfacFiles] = useState([]);
  const [dragOverKey, setDragOverKey] = useState("");

  const [busyByKey, setBusyByKey] = useState({
    ofac: false,
    un: false,
    uae: false,
  });

  const [progressByKey, setProgressByKey] = useState({
    ofac: 0,
    un: 0,
    uae: 0,
  });

  // --- CPI upload ---
  const [cpiYear, setCpiYear] = useState("2024");
  const [cpiSource, setCpiSource] = useState("Transparency International CPI 2024");
  const [importingCpi, setImportingCpi] = useState(false);
  const [cpiPreviewCount, setCpiPreviewCount] = useState(0);

  // --- Basel upload ---
  const [baselYear, setBaselYear] = useState("2025");
  const [baselSource, setBaselSource] = useState("Basel AML Index 2025");
  const [importingBasel, setImportingBasel] = useState(false);
  const [baselPreviewCount, setBaselPreviewCount] = useState(0);
  const [baselWarnings, setBaselWarnings] = useState([]);

  // --- CPI/Basel dropzone state ---
  const [cpiFile, setCpiFile] = useState(null);
  const [cpiDragOver, setCpiDragOver] = useState(false);
  const [baselFile, setBaselFile] = useState(null);
  const [baselDragOver, setBaselDragOver] = useState(false);

  // --- CPI/Basel active version meta ---
  const [cpiMeta, setCpiMeta] = useState(null);
  const [baselMeta, setBaselMeta] = useState(null);
  const [cpiProgress, setCpiProgress] = useState(0);
  const [baselProgress, setBaselProgress] = useState(0);

  // --- Proof of address utility issuer database ---
  const [utilityIssuerFile, setUtilityIssuerFile] = useState(null);
  const [utilityIssuerDragOver, setUtilityIssuerDragOver] = useState(false);
  const [utilityIssuerImporting, setUtilityIssuerImporting] = useState(false);
  const [utilityIssuerImportMeta, setUtilityIssuerImportMeta] = useState(null);
  const [utilityIssuerImportPreview, setUtilityIssuerImportPreview] = useState(null);


  // ✅ admin gate (same logic as your AdminRiskSettingsPage)
  async function assertAdminOrThrow() {
    const u = auth.currentUser;
    if (!u) throw new Error("You must be signed in.");

    const snap = await getDoc(doc(db, "users", u.uid));
    if (!snap.exists()) throw new Error("User profile missing.");

    const data = snap.data();
    if (data?.disabled) throw new Error("Your account is disabled.");
    if (data?.role !== "admin") throw new Error("Access denied: admin only.");
  }
  // async function assertAdminOrThrow() {
  //   const u = auth.currentUser;
  //   if (!u) throw new Error("You must be signed in.");

  //   const snap = await getDoc(doc(db, "users", u.uid));
  //   if (!snap.exists()) throw new Error("User profile missing.");

  //   const data = snap.data();
  //   if (data?.disabled) throw new Error("Your account is disabled.");
  //   if (!data?.isAdmin) throw new Error("Access denied: admin only.");
  // }

  async function updateSourceNow(sourceKey) {
    setRefreshRequestByKey(previous => ({ ...previous, [sourceKey]: true }));
    try {
      await requestSanctionsSourceRefresh({ sourceKey, user: auth.currentUser, apiBase: MYKYC_API_BASE });
      toastRef.current?.show({ severity: "info", summary: "Update requested", detail: "The official source will be checked. Its status updates here automatically.", life: 5000 });
    } catch (error) {
      toastRef.current?.show({ severity: "error", summary: "Update not requested", detail: error.message, life: 7000 });
    } finally {
      setRefreshRequestByKey(previous => ({ ...previous, [sourceKey]: false }));
    }
  }

  async function loadUtilityIssuerImportMeta() {
    try {
      const [metaSnap, countSnap, issuerSnap] = await Promise.all([
        getDoc(doc(db, PROOF_OF_ADDRESS_ISSUER_IMPORT_META_COLLECTION, "latest")),
        getCountFromServer(collection(db, PROOF_OF_ADDRESS_ISSUER_COLLECTION)),
        getDocs(collection(db, PROOF_OF_ADDRESS_ISSUER_COLLECTION)),
      ]);
      const meta = metaSnap.exists() ? metaSnap.data() || {} : {};
      const fallbackTypeCounts = {};
      issuerSnap.forEach((snap) => {
        const issuerTypes = snap.data()?.issuerTypes;
        if (Array.isArray(issuerTypes) && issuerTypes.length) {
          issuerTypes.forEach((type) => incrementUtilityTypeCount(fallbackTypeCounts, type));
        } else {
          incrementUtilityTypeCount(fallbackTypeCounts, "other");
        }
      });
      setUtilityIssuerImportMeta({
        ...meta,
        issuerCount: countSnap.data().count || meta.issuerCount || 0,
        typeCounts: Object.keys(meta.typeCounts || {}).length ? meta.typeCounts : fallbackTypeCounts,
      });
    } catch (e) {
      console.warn("[proof-of-address issuers] Failed to load import metadata:", e?.message || e);
      setUtilityIssuerImportMeta(null);
    }
  }



  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  async function loadMeta() {
    setErr("");
    setLoading(true);

    console.log("[loadMeta] start");

    // try {
    //   await assertAdminOrThrow();

    //   const [ofacSnap, unSnap, uaeSnap] = await Promise.all([
    //     getDoc(doc(db, "sanctionsMeta", "ofac")),
    //     getDoc(doc(db, "sanctionsMeta", "un")),
    //     getDoc(doc(db, "sanctionsMeta", "uae")),
    //   ]);

    //   setMeta({
    //     ofac: ofacSnap.exists() ? ofacSnap.data() : null,
    //     un: unSnap.exists() ? unSnap.data() : null,
    //     uae: uaeSnap.exists() ? uaeSnap.data() : null,
    //   });

    //   console.log("[loadMeta] done");
    // } catch (e) {
    //   setErr(e?.message || "Failed to load sanctions metadata.");

    try {
      await assertAdminOrThrow();

      const refs = {
        ofac: doc(db, "sanctions", "ofac"),
        un: doc(db, "sanctions", "un"),
        uae: doc(db, "sanctions", "uae"),
      };

      console.log("[loadMeta] reading docs:", {
        ofac: refs.ofac.path,
        un: refs.un.path,
        uae: refs.uae.path,
      });

      const [ofacSnap, unSnap, uaeSnap] = await Promise.all([
        getDoc(refs.ofac),
        getDoc(refs.un),
        getDoc(refs.uae),
      ]);

      console.log("[loadMeta] exists:", {
        ofac: ofacSnap.exists(),
        un: unSnap.exists(),
        uae: uaeSnap.exists(),
      });

      setMeta({
        ofac: ofacSnap.exists() ? ofacSnap.data() : null,
        un: unSnap.exists() ? unSnap.data() : null,
        uae: uaeSnap.exists() ? uaeSnap.data() : null,
      });

      console.log("[loadMeta] done");
    } catch (e) {
      logFbErr("loadMeta", e);

      // This is the key: show code + message in UI
      const msg = `${e?.code ? `${e.code}: ` : ""}${
        e?.message || "Failed to load sanctions metadata."
      }`;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMeta();
    loadUtilityIssuerImportMeta();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = [];
    async function listenToSources() {
      await assertAdminOrThrow();
      if (cancelled) return;
      unsubscribe = LISTS.map(({ key }) => onSnapshot(doc(db, "sanctions", key), (snapshot) => {
        if (!cancelled) setMeta((previous) => ({ ...previous, [key]: snapshot.exists() ? snapshot.data() : null }));
      }, () => {
        if (!cancelled) setErr("Live source updates are unavailable. Use Refresh to check the latest status.");
      }));
    }
    listenToSources().catch(() => {}); // The existing admin gate displays access errors.
    return () => { cancelled = true; unsubscribe.forEach((stop) => stop()); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    async function loadImportMeta() {
      try {
        const [cpiSnap, baselSnap] = await Promise.all([
          getDoc(doc(db, "countryRisk", `cpi_${cpiYear}`)),
          getDoc(doc(db, "countryRisk", `basel_${baselYear}`)),
        ]);
        if (cpiSnap.exists()) {
          const d = cpiSnap.data();
          setCpiMeta({
            source: d.source || "",
            year: d.year || cpiYear,
            count: d.scores ? Object.keys(d.scores).length : 0,
            updatedAt: d.updatedAt?.toDate?.() || null,
          });
        } else {
          setCpiMeta(null);
        }
        if (baselSnap.exists()) {
          const d = baselSnap.data();
          setBaselMeta({
            source: d.source || "",
            year: d.year || baselYear,
            count: d.scores ? Object.keys(d.scores).length : 0,
            updatedAt: d.updatedAt?.toDate?.() || null,
          });
        } else {
          setBaselMeta(null);
        }
      } catch (_) {}
    }
    loadImportMeta();
  }, [cpiYear, baselYear]);

  const anyBusy = useMemo(
    () =>
      Object.values(busyByKey).some(Boolean) ||
      utilityIssuerImporting,
    [busyByKey, utilityIssuerImporting]
  );

  function selectUploadFile(key, input) {
    const files = input ? (input.name ? [input] : Array.from(input)) : [];
    const file = files[0] || null;
    if (key === "ofac") setOfacFiles(files);
    setFileByKey((p) => ({
      ...p,
      [key]: file || null,
    }));
  }

  function handleFileDrop(e, key) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverKey("");

    if (busyByKey[key] || anyBusy) return;

    const file = key === "ofac" ? e.dataTransfer?.files : e.dataTransfer?.files?.[0] || null;
    if (!file) return;
    selectUploadFile(key, file);
  }

  async function uploadList(key) {
    console.log("[UPLOAD] 0) click", { key });
    if (busyByKey[key]) return;

    setErr("");
    try {
      console.log("[UPLOAD] 0.1) auth.currentUser", {
        uid: auth.currentUser?.uid || null,
        email: auth.currentUser?.email || null,
      });
      console.log("[UPLOAD] 1) assertAdminOrThrow: start");
      await assertAdminOrThrow();
      console.log("[UPLOAD] 2) assertAdminOrThrow: OK");
    } catch (e) {
      setErr(e?.message || "Access denied.");
      return;
    }

    const file = fileByKey[key];
    if (!file) {
      console.log("[UPLOAD] stop: no file");
      toastRef.current?.show({
        severity: "warn",
        summary: "Select a file",
        detail: "Please choose a file before uploading.",
        life: 3000,
      });
      return;
    }

    if (key === "ofac") {
      setBusyByKey(p => ({ ...p, ofac: true }));
      setProgressByKey(p => ({ ...p, ofac: 0 }));
      try {
        await importOfacFiles({ files: ofacFiles, user: auth.currentUser, apiBase: MYKYC_API_BASE });
        setProgressByKey(p => ({ ...p, ofac: 100 }));
        selectUploadFile("ofac", null);
        toastRef.current?.show({ severity: "success", summary: "OFAC imported", detail: "Listings and aliases imported successfully", life: 3000 });
      } catch (error) { setErr(error.message || "OFAC import failed"); }
      finally { setBusyByKey(p => ({ ...p, ofac: false })); }
      return;
    }

    const stamp = nowStamp();
    const safeName = String(file.name || "file").replace(/[^\w.\-]+/g, "_");
    const storagePath = `sanctions/raw/${key}/${stamp}_${safeName}`;
    console.log("[UPLOAD] 3) storagePath ready", { storagePath });

    console.log("[uploadList] start", {
      key,
      fileName: file?.name,
      storagePath,
    });

    setBusyByKey((p) => ({ ...p, [key]: true }));
    setProgressByKey((p) => ({ ...p, [key]: 0 }));

    try {
      console.log("[UPLOAD] 1) start upload", { key, storagePath });

      const storageRef = ref(storage, storagePath);

      console.log("before setDoc sanctions");

      await setDoc(
        doc(db, "sanctions", key),
        {
          parseStatus: "uploading",
          parseMessage: "Uploading file…",
          recordCount: null,
          active: {
            storagePath, // add this early
            importMethod: "manual",
            sourceUrl: null,
            sha256: null,
            parserVersion: null,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      console.log("after setDoc sanctions");

      // await setDoc(
      //   doc(db, "sanctions", key),
      //   {
      //     parseStatus: "uploading",
      //     parseMessage: "Uploading file…",
      //     recordCount: null,
      //     updatedAt: serverTimestamp(),
      //   },
      //   { merge: true }
      // );

      const task = uploadBytesResumable(storageRef, file, {
        contentType: file.type || undefined,
      });

      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct =
              snap.totalBytes > 0
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
            setProgressByKey((p) => ({ ...p, [key]: pct }));
          },
          (e) => reject(e),
          () => resolve()
        );
      });

      console.log("[UPLOAD] 5) uploadBytesResumable: OK");
      console.log("[UPLOAD] 2) uploadBytesResumable OK");

      const downloadUrl = await getDownloadURL(storageRef);

      console.log("[UPLOAD] 3) getDownloadURL OK");

      console.log(
        "[uploadList] writing sanctionsMeta doc:",
        `sanctionsMeta/${key}`
      );

      // ✅ store active version pointer + parsing status for backend/CF to update later
      await setDoc(
        doc(db, "sanctions", key),
        {
          key,
          title: LISTS.find((x) => x.key === key)?.title || key,
          active: {
            fileName: file.name,
            sizeBytes: file.size,
            contentType: file.type || "",
            storagePath,
            downloadUrl,
            importMethod: "manual",
            sourceUrl: null,
            referenceUrl: null,
            sourceLastModified: null,
            sha256: null,
            parserVersion: null,
          },
          // parseStatus: "pending",
          // parseMessage: "",
          // recordCount: null,
          uploadedAt: serverTimestamp(),
          uploadedByUid: auth.currentUser?.uid || "",
          uploadedByEmail: auth.currentUser?.email || "",
        },
        { merge: true }
      );

      console.log("[UPLOAD] 4) setDoc sanctions OK");

      setFileByKey((p) => ({ ...p, [key]: null }));

      toastRef.current?.show({
        severity: "success",
        summary: "Uploaded",
        detail: `${
          LISTS.find((x) => x.key === key)?.title || key
        } uploaded. Parsing pending.`,
        life: 3000,
      });

      await loadMeta();
    } catch (e) {
      console.error("[UPLOAD] ERROR", {
        message: e?.message,
        code: e?.code,
        name: e?.name,
        stack: e?.stack,
      });
      logFbErr(`uploadList:${key}`, e);
      toastRef.current?.show({
        severity: "error",
        summary: "Upload failed",
        detail: e?.message || "Upload failed.",
        life: 4500,
      });
    } finally {
      setBusyByKey((p) => ({ ...p, [key]: false }));
      setProgressByKey((p) => ({ ...p, [key]: 0 }));
    }
  }

  // ---- CPI CSV import ----
  async function handleCpiFile(file) {
    if (!file || importingCpi) return;
    setErr("");
    setImportingCpi(true);
    setCpiPreviewCount(0);
    setCpiProgress(10);
    try {
      await assertAdminOrThrow();
      setCpiProgress(25);

      const scores = {};
      await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            try {
              for (const row of results.data || []) {
                const iso3 = String(row.iso3 || row.ISO3 || "").trim().toUpperCase();
                const scoreRaw = row.cpiScore ?? row.score ?? row.CPI ?? row.cpi;
                if (!iso3 || iso3.length !== 3) continue;
                const score = Number(scoreRaw);
                if (!Number.isFinite(score)) continue;
                scores[iso3] = score;
              }
              resolve();
            } catch (e) { reject(e); }
          },
          error: (err) => reject(err),
        });
      });

      setCpiProgress(65);
      const count = Object.keys(scores).length;
      setCpiPreviewCount(count);
      if (count === 0) throw new Error("No CPI rows parsed. Check CSV headers: iso3, cpiScore");

      const yearNum = Number(cpiYear);
      if (!Number.isFinite(yearNum)) throw new Error("Invalid year.");
      const sourceLabel = String(cpiSource || "").trim() || `CPI ${yearNum}`;

      await setDoc(doc(db, "countryRisk", `cpi_${yearNum}`), {
        year: yearNum,
        source: sourceLabel,
        scores,
        updatedAt: serverTimestamp(),
      });

      setCpiProgress(100);
      setCpiMeta({ source: sourceLabel, year: yearNum, count, updatedAt: new Date() });
      setTimeout(() => setCpiProgress(0), 2000);

      toastRef.current?.show({
        severity: "success",
        summary: "CPI imported",
        detail: `Imported ${count} country scores into countryRisk/cpi_${yearNum}`,
        life: 3500,
      });
    } catch (e) {
      setCpiProgress(0);
      toastRef.current?.show({
        severity: "error",
        summary: "Import failed",
        detail: e?.message || "Failed to import CPI",
        life: 5000,
      });
    } finally {
      setImportingCpi(false);
      setCpiFile(null);
    }
  }

  // ---- Basel AML Index import (XLS / XLSX / CSV) ----
  async function handleBaselFile(file) {
    if (!file || importingBasel) return;
    setErr("");
    setImportingBasel(true);
    setBaselPreviewCount(0);
    setBaselWarnings([]);
    setBaselProgress(10);
    try {
      await assertAdminOrThrow();
      setBaselProgress(25);

      // Build name→ISO3 lookup from i18n-iso-countries
      const nameToIso3 = {};
      const allNames = countries.getNames("en");
      Object.entries(allNames).forEach(([alpha2, name]) => {
        const iso3 = countries.alpha2ToAlpha3(alpha2);
        if (iso3 && name) nameToIso3[normalizeCountryLookupName(name)] = iso3;
      });

      // Manual aliases for Basel-specific naming
      const ALIASES = {
        "myanmar": "MMR",
        "russia": "RUS",
        "syria": "SYR",
        "vietnam": "VNM",
        "bolivia": "BOL",
        "iran": "IRN",
        "laos": "LAO",
        "lao pdr": "LAO",
        "moldova": "MDA",
        "tanzania": "TZA",
        "venezuela": "VEN",
        "korea, north": "PRK",
        "korea, south": "KOR",
        "hong kong": "HKG",
        "macao": "MAC",
        "macau": "MAC",
        "ivory coast": "CIV",
        "cote d'ivoire": "CIV",
        "côte d'ivoire": "CIV",
        "east timor": "TLS",
        "timor-leste": "TLS",
        "swaziland": "SWZ",
        "eswatini": "SWZ",
        "cape verde": "CPV",
        "czechia": "CZE",
        "czech republic": "CZE",
        "north macedonia": "MKD",
        "republic of north macedonia": "MKD",
        "republic of the congo": "COG",
        "congo, republic of": "COG",
        "democratic republic of the congo": "COD",
        "democratic republic of congo": "COD",
        "congo, democratic republic of": "COD",
        "dr congo": "COD",
        "gambia": "GMB",
        "the gambia": "GMB",
        "central african republic": "CAF",
        "guinea-bissau": "GNB",
        "equatorial guinea": "GNQ",
        "st. lucia": "LCA",
        "china": "CHN",
        "turkey": "TUR",
        "türkiye": "TUR",
        "turkiye": "TUR",
        "brunei": "BRN",
        "macedonia": "MKD",
        "bosnia-herzegovina": "BIH",
        "st. vincent and the grenadines": "VCT",
        "hong kong sar, china": "HKG",
        "united states": "USA",
        "taiwan, china": "TWN",
        "taiwan": "TWN",
      };

      // Parse rows from XLS/XLSX or CSV — returns array of plain objects
      async function parseBaselRows(f) {
        const isExcel = /\.(xls|xlsx)$/i.test(f.name);
        if (isExcel) {
          const buffer = await f.arrayBuffer();
          const wb = XLSX.read(buffer, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          // sheet_to_json with header:1 gives raw rows as arrays
          const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

          // Find the header row: first row where a cell matches /country/i
          let headerRowIdx = -1;
          for (let i = 0; i < raw.length; i++) {
            if (raw[i].some((cell) => /^country$/i.test(String(cell).trim()))) {
              headerRowIdx = i;
              break;
            }
          }
          if (headerRowIdx === -1) throw new Error('No header row with "Country" found in the spreadsheet.');

          const headers = raw[headerRowIdx].map((c) => String(c).trim());
          const dataRows = raw.slice(headerRowIdx + 1);
          return dataRows.map((row) => {
            const obj = {};
            headers.forEach((h, i) => { obj[h] = row[i] ?? ""; });
            return obj;
          });
        } else {
          return await new Promise((resolve, reject) => {
            Papa.parse(f, {
              header: true,
              skipEmptyLines: true,
              complete: (results) => resolve(results.data || []),
              error: (err) => reject(err),
            });
          });
        }
      }

      const rows = await parseBaselRows(file);

      const scores = {};
      const unmatched = [];
      let detectedYear = null;

      // Detect year and column names from the first row's keys
      const sampleKeys = Object.keys(rows[0] || {});
      for (const h of sampleKeys) {
        const m = String(h).match(/overall\s+score\s+(\d{4})/i);
        if (m) { detectedYear = m[1]; break; }
      }
      const scoreCol = sampleKeys.find((h) => /overall\s+score/i.test(h));
      const nameCol = sampleKeys.find((h) => /^country$/i.test(h.trim()));

      if (!scoreCol) throw new Error(`No "Overall score" column found. Detected columns: ${sampleKeys.join(", ")}`);
      if (!nameCol) throw new Error(`No "Country" column found. Detected columns: ${sampleKeys.join(", ")}`);

      for (const row of rows) {
        const rawName = String(row[nameCol] || "").trim();
        if (!rawName) continue;
        const score = parseSpreadsheetNumber(row[scoreCol]);
        if (!Number.isFinite(score)) continue;
        const lowerName = normalizeCountryLookupName(rawName);
        const iso3 = ALIASES[lowerName] || nameToIso3[lowerName] || null;
        if (!iso3) { unmatched.push(rawName); continue; }
        scores[iso3] = score;
      }

      setBaselProgress(65);
      const count = Object.keys(scores).length;
      setBaselPreviewCount(count);
      setBaselWarnings(unmatched);
      if (count === 0) throw new Error("No Basel rows parsed. Check CSV headers: Country, Overall score YYYY");

      const yearNum = Number(baselYear);
      if (!Number.isFinite(yearNum)) throw new Error("Enter a valid Basel year before importing.");

      if (detectedYear && String(detectedYear) !== String(yearNum)) {
        console.warn(
          `[Basel AML import] Spreadsheet header year ${detectedYear} ignored; using selected year ${yearNum}.`
        );
      }
      const sourceLabel = String(baselSource || "").trim() || `Basel AML Index ${yearNum}`;

      await setDoc(doc(db, "countryRisk", `basel_${yearNum}`), {
        year: yearNum,
        source: sourceLabel,
        scores,
        updatedAt: serverTimestamp(),
      });

      setBaselProgress(100);
      setBaselMeta({ source: sourceLabel, year: yearNum, count, updatedAt: new Date() });
      setTimeout(() => setBaselProgress(0), 2000);

      toastRef.current?.show({
        severity: "success",
        summary: "Basel AML imported",
        detail: `Imported ${count} country scores into countryRisk/basel_${yearNum}${unmatched.length ? ` (${unmatched.length} unmatched)` : ""}`,
        life: 4000,
      });
    } catch (e) {
      setBaselProgress(0);
      toastRef.current?.show({
        severity: "error",
        summary: "Import failed",
        detail: e?.message || "Failed to import Basel AML Index",
        life: 5000,
      });
    } finally {
      setImportingBasel(false);
      setBaselFile(null);
    }
  }

  async function handlePreviewUtilityIssuerImport(file) {
    if (!file) {
      setUtilityIssuerFile(null);
      setUtilityIssuerImportPreview(null);
      return;
    }
    try {
      const parsed = await parseUtilityIssuerWorkbook(file);
      setUtilityIssuerFile(file);
      setUtilityIssuerImportPreview({
        sourceRowCount: parsed.sourceRowCount,
        issuerCount: parsed.issuers.length,
        typeCounts: parsed.typeCounts,
        skippedRows: parsed.skippedRows,
      });
    } catch (e) {
      setUtilityIssuerFile(null);
      setUtilityIssuerImportPreview(null);
      toastRef.current?.show({
        severity: "error",
        summary: "Preview failed",
        detail: e?.message || "Failed to read utility issuer spreadsheet",
        life: 5000,
      });
    }
  }

  async function handleImportUtilityIssuers() {
    if (!utilityIssuerFile || utilityIssuerImporting) return;

    try {
      setUtilityIssuerImporting(true);
      const parsed = await parseUtilityIssuerWorkbook(utilityIssuerFile);
      if (!parsed.issuers.length) {
        throw new Error("No valid utility issuers found in this spreadsheet.");
      }

      const importedBy = auth.currentUser?.email || auth.currentUser?.uid || "";
      const existingSnap = await getDocs(collection(db, PROOF_OF_ADDRESS_ISSUER_COLLECTION));
      const ops = [];
      existingSnap.forEach((snap) => {
        ops.push({ type: "delete", ref: snap.ref });
      });
      parsed.issuers.forEach((issuer) => {
        const { id, ...data } = issuer;
        ops.push({
          type: "set",
          ref: doc(db, PROOF_OF_ADDRESS_ISSUER_COLLECTION, id),
          data: {
            ...data,
            importedAt: serverTimestamp(),
            importedBy,
            updatedAt: serverTimestamp(),
          },
        });
      });

      await commitFirestoreOperations(ops);
      await setDoc(doc(db, PROOF_OF_ADDRESS_ISSUER_IMPORT_META_COLLECTION, "latest"), {
        sourceFileName: utilityIssuerFile.name,
        sourceRowCount: parsed.sourceRowCount,
        issuerCount: parsed.issuers.length,
        typeCounts: parsed.typeCounts,
        skippedRowCount: parsed.skippedRows.length,
        skippedRows: parsed.skippedRows.slice(0, 100),
        importedAt: serverTimestamp(),
        importedBy,
      });

      setUtilityIssuerImportMeta({
        sourceFileName: utilityIssuerFile.name,
        sourceRowCount: parsed.sourceRowCount,
        issuerCount: parsed.issuers.length,
        typeCounts: parsed.typeCounts,
        skippedRowCount: parsed.skippedRows.length,
        skippedRows: parsed.skippedRows.slice(0, 100),
        importedAt: new Date(),
        importedBy,
      });
      setUtilityIssuerFile(null);
      setUtilityIssuerImportPreview(null);

      toastRef.current?.show({
        severity: "success",
        summary: "Utility issuers imported",
        detail: `Imported ${parsed.issuers.length} issuer(s) from ${parsed.sourceRowCount} row(s).`,
        life: 4000,
      });
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Import failed",
        detail: e?.message || "Failed to import utility issuers",
        life: 5000,
      });
    } finally {
      setUtilityIssuerImporting(false);
    }
  }

  // ******************* RENDER HTML ********************

  return (
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="adminUsersPage"
    >
      {/* TOP BAR (same pattern as AdminRiskSettingsPage) */}
      <div className="adminUsersTitleBar">
        <div className="container py-3">
          <div className="clientsHeader d-flex align-items-center justify-content-between">
            <div>
              <h2 className="clientsHeaderTitle m-0">
                ADMIN · Sources files
              </h2>
              <div className="small" style={{ color: "#fff", opacity: 0.8 }}>
                Upload OFAC / UN / UAE / CPI / Basel lists (raw files). Backend will parse and
                index them for screening.
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => nav("/clients")}
                style={{ whiteSpace: "nowrap" }}
                disabled={anyBusy}
              >
                ← Back
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={loadMeta}
                disabled={loading || anyBusy}
              >
                <i className="bi bi-arrow-repeat me-2" />
                Refresh
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleLogout}
                style={{ whiteSpace: "nowrap" }}
                disabled={anyBusy}
              >
                <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="container py-4">
        {err ? <div className="alert alert-danger">{err}</div> : null}

        {loading ? (
          <div className="card p-4 text-muted">Loading…</div>
        ) : (
          <>
            {/* MAIN CARD */}
            <div className="card shadow-sm mb-3">
              <div className="p-3 border-bottom">
                <div className="fw-bold" style={{ color: "#4C5465" }}>
                  Upload sanctions source files
                </div>
                <div className="small text-muted">
                  Review the active lists and automatic refresh status, or upload a source file manually.
                </div>
              </div>

              <div className="p-3">
                <div className="row g-3 adminSanctionsSourcesGrid">
                  {LISTS.map((L) => {
                    const m = meta[L.key] || {};
                    const active = m?.active || null;
                    const busy = !!busyByKey[L.key];
                    const progress = progressByKey[L.key] || 0;
                    return (
                      <div key={L.key} className="col-12 col-lg-4">
                        <div className="card shadow-sm h-100 adminSanctionsSourceCard">
                          <div className="p-3 border-bottom adminSanctionsSourceHeader">
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <div>
                                <div
                                  className="fw-bold"
                                  style={{ color: "#4C5465" }}
                                >
                                  {L.title}
                                </div>
                                <div className="small text-muted">{L.help}</div>
                              </div>
                              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                <SanctionsSourceStatusBadge sourceKey={L.key} meta={m} now={sourceAgeNow} />
                                <SanctionsSourceAgeBadge meta={m} now={sourceAgeNow} />
                              </div>
                            </div>
                            <div className="adminSanctionsSourceActions">
                              <SanctionsSourceReferences sourceKey={L.key} active={active}>
                                <SanctionsSourceUpdateButton title={L.title} meta={m} requesting={refreshRequestByKey[L.key]}
                                  disabled={busy || anyBusy} onUpdate={() => updateSourceNow(L.key)} />
                              </SanctionsSourceReferences>
                            </div>
                          </div>

                          <div className="p-3 adminSanctionsSourceBody">
                            <div className="adminSanctionsActiveBlock">
                              <SanctionsActiveSource sourceKey={L.key} meta={m}
                                formatBytes={prettyBytes} formatDate={formatAdminDateTime} />
                            </div>

                            <div className="adminSanctionsUploadDivider" />

                            <div className="adminSanctionsUploadBlock">
                              <div className="text-muted small mb-1 adminSanctionsUploadLabel">
                                Upload new file
                              </div>
                              <div
                                className={`adminSanctionsDropzone${
                                  dragOverKey === L.key ? " isDragOver" : ""
                                }${busy || anyBusy ? " isDisabled" : ""}`}
                                onDragEnter={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!busy && !anyBusy) setDragOverKey(L.key);
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!busy && !anyBusy) setDragOverKey(L.key);
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (e.currentTarget.contains(e.relatedTarget)) return;
                                  setDragOverKey("");
                                }}
                                onDrop={(e) => handleFileDrop(e, L.key)}
                              >
                                <div className="adminSanctionsDropzoneIcon">
                                  <i className="bi bi-cloud-arrow-up" />
                                </div>
                                <div className="adminSanctionsDropzoneText">
                                  Drag and drop file here
                                </div>
                                <div className="adminSanctionsDropzoneHint">
                                  or browse from your computer
                                </div>
                                {!fileByKey[L.key] ? (
                                  <input
                                    type="file"
                                    className="form-control adminSanctionsFileInput"
                                    accept={L.accept}
                                    multiple={false}
                                    disabled={busy || anyBusy}
                                    onChange={(e) =>
                                      selectUploadFile(
                                        L.key,
                                        L.key === "ofac" ? e.target.files : e.target.files?.[0] || null
                                      )
                                    }
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-lightgrey adminSanctionsReplaceFileBtn"
                                    disabled={busy || anyBusy}
                                    onClick={() => selectUploadFile(L.key, null)}
                                  >
                                    Choose another file
                                  </button>
                                )}
                                {fileByKey[L.key] ? (
                                  <div className="adminSanctionsSelectedFile">
                                    {L.key === "ofac" ? ofacFiles.map(file => file.name).join(" + ") : fileByKey[L.key].name}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {busy ? (
                              <div className="mt-2 adminSanctionsUploadProgress">
                                <div className="progress">
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${progress}%` }}
                                    aria-valuenow={progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    {progress}%
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            <div className="d-flex justify-content-center gap-2 mt-2 flex-wrap adminSanctionsUploadActions">
                              <button
                                type="button"
                                className="btn btn-save1"
                                onClick={() => uploadList(L.key)}
                                disabled={busy || anyBusy}
                              >
                                {busy ? "Uploading…" : `Upload ${L.title}`}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="small text-muted mt-3">
                  Successfully imported lists are picked up by the screening service. Source updates do not change saved client screening results.
                </div>
              </div>
            </div>

            {/* CPI CARD */}
            <div className="card shadow-sm mb-3">
              <div className="p-3 border-bottom">
                <div className="fw-bold" style={{ color: "#4C5465" }}>
                  Corruption Perception Index (CPI)
                </div>
                <div className="small text-muted">
                  CSV with two columns and headers: <code>iso3</code>,{" "}
                  <code>cpiScore</code> — global data shared across all organisations
                </div>
              </div>

              <div className="p-3">
                <div className="row g-3 align-items-start">
                  {/* Active version */}
                  <div className="col-12 col-md-4">
                    <div className="text-muted small mb-2 fw-semibold">Active version</div>
                    <div className="small">
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Source</span>
                        <span style={{ color: "#4C5465", textAlign: "right" }}>{cpiMeta?.source || "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Year</span>
                        <span style={{ color: "#4C5465" }}>{cpiMeta?.year ?? "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Countries</span>
                        <span style={{ color: "#4C5465" }}>{cpiMeta?.count ?? "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Updated</span>
                        <span style={{ color: "#4C5465" }}>
                          {cpiMeta?.updatedAt ? cpiMeta.updatedAt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Upload section: source/year + dropzone, framed together */}
                  <div className="col-12 col-md-8">
                    <div style={{ border: "1px solid #DDE5EA", borderRadius: 8, padding: "12px" }}>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="text-muted small mb-1">Source label</div>
                          <input className="form-control form-control-sm mb-2" value={cpiSource} onChange={(e) => setCpiSource(e.target.value)} />
                          <div className="text-muted small mb-1">Year</div>
                          <input className="form-control form-control-sm" value={cpiYear} onChange={(e) => setCpiYear(e.target.value)} />
                        </div>
                        <div className="col-12 col-md-6">
                          <div
                            className={`adminSanctionsDropzone${cpiDragOver ? " isDragOver" : ""}${importingCpi ? " isDisabled" : ""}`}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!importingCpi) setCpiDragOver(true); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!importingCpi) setCpiDragOver(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setCpiDragOver(false); }}
                            onDrop={(e) => {
                              e.preventDefault(); e.stopPropagation(); setCpiDragOver(false);
                              if (importingCpi) return;
                              const f = e.dataTransfer?.files?.[0] || null;
                              if (f) { setCpiFile(f); handleCpiFile(f); }
                            }}
                          >
                            <div className="adminSanctionsDropzoneIcon"><i className="bi bi-cloud-arrow-up" /></div>
                            <div className="adminSanctionsDropzoneText">Drag and drop file here</div>
                            <div className="adminSanctionsDropzoneHint">or browse from your computer</div>
                            {!cpiFile ? (
                              <input type="file" className="form-control adminSanctionsFileInput" accept=".csv,text/csv" disabled={importingCpi}
                                onChange={(e) => { const f = e.target.files?.[0] || null; if (f) { setCpiFile(f); handleCpiFile(f); } }} />
                            ) : (
                              <button type="button" className="btn btn-sm btn-outline-lightgrey adminSanctionsReplaceFileBtn" disabled={importingCpi} onClick={() => setCpiFile(null)}>
                                Choose another file
                              </button>
                            )}
                            {cpiFile ? <div className="adminSanctionsSelectedFile">{cpiFile.name}</div> : null}
                          </div>
                          {cpiProgress > 0 ? (
                            <div className="mt-2">
                              <div className="progress">
                                <div
                                  className={`progress-bar${importingCpi ? " progress-bar-animated progress-bar-striped" : ""}`}
                                  role="progressbar"
                                  style={{ width: `${cpiProgress}%`, backgroundColor: "#A8B5C2" }}
                                  aria-valuenow={cpiProgress} aria-valuemin="0" aria-valuemax="100"
                                >
                                  {cpiProgress}%
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BASEL AML INDEX CARD */}
            <div className="card shadow-sm mb-3">
              <div className="p-3 border-bottom">
                <div className="fw-bold" style={{ color: "#4C5465" }}>
                  Basel AML Index
                </div>
                <div className="small text-muted">
                  XLS / XLSX / CSV with columns: <code>Country</code>,{" "}
                  <code>Overall score YYYY</code> — score 0–10 (10 = highest
                  risk) — global data shared across all organisations
                </div>
              </div>

              <div className="p-3">
                <div className="row g-3 align-items-start">
                  {/* Active version */}
                  <div className="col-12 col-md-4">
                    <div className="text-muted small mb-2 fw-semibold">Active version</div>
                    <div className="small">
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Source</span>
                        <span style={{ color: "#4C5465", textAlign: "right" }}>{baselMeta?.source || "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Year</span>
                        <span style={{ color: "#4C5465" }}>{baselMeta?.year ?? "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Countries</span>
                        <span style={{ color: "#4C5465" }}>{baselMeta?.count ?? "—"}</span>
                      </div>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted">Updated</span>
                        <span style={{ color: "#4C5465" }}>
                          {baselMeta?.updatedAt ? baselMeta.updatedAt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Upload section: source/year + dropzone, framed together */}
                  <div className="col-12 col-md-8">
                    <div style={{ border: "1px solid #DDE5EA", borderRadius: 8, padding: "12px" }}>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="text-muted small mb-1">Source label</div>
                          <input className="form-control form-control-sm mb-2" value={baselSource} onChange={(e) => setBaselSource(e.target.value)} />
                          <div className="text-muted small mb-1">Year</div>
                          <input className="form-control form-control-sm" value={baselYear} onChange={(e) => setBaselYear(e.target.value)} />
                        </div>
                        <div className="col-12 col-md-6">
                          <div
                            className={`adminSanctionsDropzone${baselDragOver ? " isDragOver" : ""}${importingBasel ? " isDisabled" : ""}`}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!importingBasel) setBaselDragOver(true); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!importingBasel) setBaselDragOver(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setBaselDragOver(false); }}
                            onDrop={(e) => {
                              e.preventDefault(); e.stopPropagation(); setBaselDragOver(false);
                              if (importingBasel) return;
                              const f = e.dataTransfer?.files?.[0] || null;
                              if (f) { setBaselFile(f); handleBaselFile(f); }
                            }}
                          >
                            <div className="adminSanctionsDropzoneIcon"><i className="bi bi-cloud-arrow-up" /></div>
                            <div className="adminSanctionsDropzoneText">Drag and drop file here</div>
                            <div className="adminSanctionsDropzoneHint">or browse from your computer</div>
                            {!baselFile ? (
                              <input type="file" className="form-control adminSanctionsFileInput" accept=".csv,.xls,.xlsx,text/csv" disabled={importingBasel}
                                onChange={(e) => { const f = e.target.files?.[0] || null; if (f) { setBaselFile(f); handleBaselFile(f); } }} />
                            ) : (
                              <button type="button" className="btn btn-sm btn-outline-lightgrey adminSanctionsReplaceFileBtn" disabled={importingBasel} onClick={() => setBaselFile(null)}>
                                Choose another file
                              </button>
                            )}
                            {baselFile ? <div className="adminSanctionsSelectedFile">{baselFile.name}</div> : null}
                          </div>
                          {baselProgress > 0 ? (
                            <div className="mt-2">
                              <div className="progress">
                                <div
                                  className={`progress-bar${importingBasel ? " progress-bar-animated progress-bar-striped" : ""}`}
                                  role="progressbar"
                                  style={{ width: `${baselProgress}%`, backgroundColor: "#A8B5C2" }}
                                  aria-valuenow={baselProgress} aria-valuemin="0" aria-valuemax="100"
                                >
                                  {baselProgress}%
                                </div>
                              </div>
                            </div>
                          ) : null}
                          {baselWarnings.length > 0 ? (
                            <div className="small mt-2 text-warning">
                              <strong>{baselWarnings.length} unmatched:</strong>{" "}
                              {baselWarnings.join(", ")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PROOF OF ADDRESS UTILITY ISSUER DATABASE CARD */}
            <div className="card shadow-sm mb-3">
              <div className="p-3 border-bottom d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <div>
                  <div className="fw-bold" style={{ color: "#4C5465" }}>
                    Utility issuer database
                  </div>
                  <div className="small text-muted">
                    XLS / XLSX with columns: <code>ISO2</code>, <code>Country</code>,{" "}
                    <code>Company</code>, <code>Utility Type</code> — used later by proof-of-address checks
                    {utilityIssuerImportMeta?.issuerCount > 0 ? (
                      <span className="ms-2">· {utilityIssuerImportMeta.issuerCount} issuer(s) loaded</span>
                    ) : null}
                  </div>
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={loadUtilityIssuerImportMeta}
                  disabled={utilityIssuerImporting}
                >
                  <i className="bi bi-arrow-repeat me-2" />
                  Refresh
                </button>
              </div>

              <div className="p-3">
                <div className="row g-3 align-items-start">
                  <div className="col-12 col-lg-4">
                    <div className="text-muted small mb-2 fw-semibold">Active version</div>
                    <div className="utilityIssuerImportSummary">
                      {utilityIssuerImportPreview ? (
                        <>
                          <div>
                            Preview: {utilityIssuerImportPreview.issuerCount} issuer(s) from{" "}
                            {utilityIssuerImportPreview.sourceRowCount} row(s)
                          </div>
                          {sortedUtilityTypeCounts(utilityIssuerImportPreview.typeCounts).length > 0 ? (
                            <div className="mt-2 d-flex flex-wrap gap-1">
                              {sortedUtilityTypeCounts(utilityIssuerImportPreview.typeCounts).map(([type, count]) => (
                                <span key={type} className="badge text-bg-light border">
                                  {utilityTypeLabel(type)}: {count}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {utilityIssuerImportPreview.skippedRows.length > 0 ? (
                            <div className="text-warning">
                              {utilityIssuerImportPreview.skippedRows.length} row(s) skipped
                            </div>
                          ) : null}
                        </>
                      ) : utilityIssuerImportMeta?.sourceFileName ? (
                        <>
                          <div>Last import: {utilityIssuerImportMeta.sourceFileName}</div>
                          <div>{formatAdminDateTime(utilityIssuerImportMeta.importedAt)}</div>
                          {sortedUtilityTypeCounts(utilityIssuerImportMeta.typeCounts).length > 0 ? (
                            <div className="mt-2 d-flex flex-wrap gap-1">
                              {sortedUtilityTypeCounts(utilityIssuerImportMeta.typeCounts).map(([type, count]) => (
                                <span key={type} className="badge text-bg-light border">
                                  {utilityTypeLabel(type)}: {count}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div>No utility issuer database imported yet.</div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-8">
                    <div style={{ border: "1px solid #DDE5EA", borderRadius: 8, padding: "12px" }}>
                      <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-8">
                          <div
                            className={`adminSanctionsDropzone${utilityIssuerDragOver ? " isDragOver" : ""}${utilityIssuerImporting ? " isDisabled" : ""}`}
                            onDragEnter={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!utilityIssuerImporting) setUtilityIssuerDragOver(true);
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!utilityIssuerImporting) setUtilityIssuerDragOver(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (e.currentTarget.contains(e.relatedTarget)) return;
                              setUtilityIssuerDragOver(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setUtilityIssuerDragOver(false);
                              if (utilityIssuerImporting) return;
                              const file = e.dataTransfer?.files?.[0] || null;
                              if (file) handlePreviewUtilityIssuerImport(file);
                            }}
                          >
                            <div className="adminSanctionsDropzoneIcon">
                              <i className="bi bi-cloud-arrow-up" />
                            </div>
                            <div className="adminSanctionsDropzoneText">Drag and drop file here</div>
                            <div className="adminSanctionsDropzoneHint">
                              XLS / XLSX with ISO2, Country, Company, Utility Type
                            </div>
                            {!utilityIssuerFile ? (
                              <input
                                type="file"
                                className="form-control adminSanctionsFileInput"
                                accept=".xlsx,.xls"
                                disabled={utilityIssuerImporting}
                                onChange={(e) => handlePreviewUtilityIssuerImport(e.target.files?.[0] || null)}
                              />
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-lightgrey adminSanctionsReplaceFileBtn"
                                disabled={utilityIssuerImporting}
                                onClick={() => handlePreviewUtilityIssuerImport(null)}
                              >
                                Choose another file
                              </button>
                            )}
                            {utilityIssuerFile ? (
                              <div className="adminSanctionsSelectedFile">{utilityIssuerFile.name}</div>
                            ) : null}
                          </div>
                        </div>

                        <div className="col-12 col-md-4 d-flex justify-content-md-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-activation"
                            disabled={!utilityIssuerFile || utilityIssuerImporting}
                            onClick={handleImportUtilityIssuers}
                          >
                            <i className="bi bi-upload me-2" />
                            {utilityIssuerImporting ? "Uploading..." : "Upload Database"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CORPORATE DOCUMENT REQUIREMENTS (maintained here, no import) */}
            <CorporateDocumentRequirementsEditor />

          <GiinDirectoryUpload />
          <TreatyCountriesEditor />
          </>
        )}
      </div>

      <Toast ref={toastRef} />
    </motion.div>
  );
}
